/**
 * KeptRangesTimeline — the older-order ranges this device keeps offline.
 *
 * TCA-26100804. Replaces a plain ISO-date list that was hard to read. The bar
 * is decorative and hidden from assistive technology, because the grouped list
 * below carries the same information in text.
 *
 * Presentational only: it receives the ranges the historical card already
 * holds, so nothing here fetches or refetches status.
 */
import React, { useMemo } from 'react';

import { buildKeptRangesTimeline } from '@/utils/keptRangesTimeline';

function todayIso() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export default function KeptRangesTimeline({ ranges = [], today = null }) {
  const model = useMemo(
    () => buildKeptRangesTimeline(ranges, today || todayIso()),
    [ranges, today],
  );

  if (model.mode === 'empty') return null;

  return (
    <div data-testid="kept-ranges-timeline">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold text-stone-700">Kept on this device</p>
        <p className="text-xs text-stone-500" data-testid="kept-ranges-totals">
          {model.totals.rangeCount} {model.totals.rangeCount === 1 ? 'range' : 'ranges'}
          {' · '}
          {model.totals.orderCount.toLocaleString()} orders
        </p>
      </div>

      {/* Decorative: the grouped list below says the same thing in words. */}
      <div className="relative mt-3 h-8" aria-hidden="true">
        <div className="absolute inset-x-0 top-3 h-1.5 rounded-full bg-stone-100" />
        {model.segments.map((segment) => (
          <div
            key={segment.key}
            className="absolute top-3 h-1.5 rounded-full bg-emerald-500"
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
          />
        ))}
        {model.ticks.map((tick) => (
          <React.Fragment key={`${tick.pos}:${tick.label || ''}`}>
            <div
              className={`absolute top-1 w-px ${tick.major ? 'h-6 bg-stone-300' : 'h-3 bg-stone-200'}`}
              style={{ left: `${tick.pos}%` }}
            />
            {tick.label && (
              <span
                className="absolute top-5 -translate-x-1/2 text-[10px] text-stone-400"
                style={{ left: `${tick.pos}%` }}
              >
                {tick.label}
              </span>
            )}
          </React.Fragment>
        ))}
        <div className="absolute right-0 top-1 h-6 w-px bg-stone-700" />
        <span className="absolute right-0 top-5 text-[10px] font-semibold text-stone-600">Today</span>
      </div>

      <div className="mt-3 space-y-3">
        {model.groups.map((group) => (
          <div key={group.year}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-emerald-800">{group.year}</span>
              <span className="h-px flex-1 bg-stone-200" />
            </div>
            <div className="mt-1 space-y-1">
              {group.rows.map((row) => (
                <div
                  key={row.key}
                  className="flex items-center gap-2 text-xs text-stone-600"
                  data-testid="kept-ranges-row"
                >
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                  <span className="font-medium text-stone-700">{row.label}</span>
                  <span className="text-stone-400">
                    {row.days} {row.days === 1 ? 'day' : 'days'}
                  </span>
                  <span className="ml-auto tabular-nums text-stone-500">
                    {row.orderCount === null ? '—' : `${row.orderCount.toLocaleString()} orders`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
